import * as React from "react";
import { useEffect, useRef, useState } from "react";
import "./Chatbot.css";
import { useAppDispatch } from "../../../../app/redux/hooks";
import {
  getWelcomMessageAsync,
  sendMessageAsync,
} from "../../../../app/slices/chatBotSlice/ChatBotSlice";
import { useNavigate } from "react-router-dom";

export const ChatPage = () => {
  const dispatch = useAppDispatch();
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const [chatId, setChatId] = useState<any>("");
  const [showTextInput, setShowTextInput] = useState(false);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  type Message = {
    text: string;
    from?: "user" | "bot";
    option_type?: string;
    options?: { id: string; value: string }[];
    redirect?: boolean;
    redirect_name?: string;
  };

  const [messages, setMessages] = useState<Message[]>([]);

  useEffect(() => {
    fetchWelcomeMessage();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isSending]);

  const fetchWelcomeMessage = async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const firstPayload = { type: "welcome" };

    try {
      const firstResponse: any = await dispatch(
        getWelcomMessageAsync(firstPayload)
      );
      const message = firstResponse?.payload?.data?.message;
      const newChatId = firstResponse?.payload?.data?.chat_id;

      if (message) {
        setMessages((prev) => [...prev, { text: message, from: "bot" }]);
      }
      setChatId(newChatId);

      if (newChatId) {
        const secondPayload = { related_chat_id: newChatId };
        const secondResponse: any = await dispatch(
          getWelcomMessageAsync(secondPayload)
        );
        setChatId(secondResponse?.payload?.data?.results[0].chat_id);
        const second = secondResponse?.payload?.data?.results[0];
        if (second?.message) {
          setMessages((prev) => [
            ...prev,
            {
              text: second.message,
              option_type: second.option_type,
              options: second.options || [],
              from: "bot",
            },
          ]);
        }
      }
    } catch (error) {
      console.error("Error fetching welcome messages:", error);
    }
  };

  const handleOptionClick = async (
    optionId: string,
    option_type: any,
    redirect: any,
    redirect_name: any
  ) => {
    if (redirect) {
      if (redirect_name === "Offers") {
        localStorage.setItem("offer_id", optionId);
        navigate(`/offerList/orders/discounts/${optionId}`);
      } else if (redirect_name === "Products") {
        navigate(`/products/${optionId}`);
      } else if (redirect_name === "Address") {
        navigate(`/store-locations/${optionId}`);
      }
    } else {
      if (!chatId) return;

      setMessages((prev) => [...prev]);

      const payload = {
        related_chat_id: chatId,
        option_type: option_type,
        option_value: optionId,
        page_size:6
      };

      try {
        const response: any = await dispatch(getWelcomMessageAsync(payload));
        if (response?.payload?.data?.results) {
          if (response?.payload?.data?.results?.[0]?.status) {
            const next = response?.payload?.data?.results?.[0];

            if (next?.message) {
              setChatId(next.chat_id);
              setMessages((prev) => [
                ...prev,
                {
                  text: next.message,
                  option_type: next.option_type,
                  options: next.options || [],
                  redirect: next.redirect,
                  redirect_name: next.redirect_name,
                },
              ]);
            }
          } else {
            setMessages((prev) => [
              ...prev,
              {
                text: "Something went wrong, contact support for assistance.",
              },
            ]);
            setShowTextInput(true);
          }
        } else {
          if (response?.payload?.data?.status) {
            if (response?.payload?.data?.message) {
              setChatId("");
              setMessages((prev) => [
                ...prev,
                {
                  text: response?.payload?.data?.message,
                },
              ]);
            }
          } else {
            setMessages((prev) => [
              ...prev,
              {
                text: "Something went wrong, contact support for assistance.",
              },
            ]);
            setShowTextInput(true);
          }
        }
      } catch (err) {
        console.error("Error on option click:", err);
      }
    }
  };

  const sendMessage = () => {
    setIsSending(true);
    if (!input.trim() || !chatId) return;
    const userMessage = input.trim();
    setInput("");
    const payload = { msg: userMessage };
    dispatch(sendMessageAsync(payload)).then((response: any) => {
      if (response?.payload?.status === 200) {
        setMessages((prev) => [...prev, { text: userMessage, from: "user" }]);
        setIsSending(false);
      }
    });
  };

  const clearChat = () => {
    setMessages([]);
    setChatId("");
    setShowTextInput(false);
    hasFetched.current = false;
    fetchWelcomeMessage();
  };
  let lastOptionMessageIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.options && m.options.length > 0) {
      lastOptionMessageIndex = i;
      break;
    }
  }

  const isLastMessageWithOptions =
    lastOptionMessageIndex === messages.length - 1;
  return (
    <div className="chat-container">
      <div className="chat-header d-flex">
        <img src="/shopnow/images/navya_new_logo.svg" width="50" alt="" />
        Navya Bakers
      </div>
      <div className="chat-messages" id="chatMessages">
        {messages.map((msg, index) => (
          <div key={index} className="message-container">
            <div
              className="message-wrapper"
              style={{
                alignSelf: msg.from === "user" ? "flex-end" : "flex-start",
              }}
            >
              <div
                className={msg.from === "user" ? "user-message" : "bot-message"}
              >
                {msg.text}
              </div>
            </div>

            {msg.options && msg.options.length > 0 && (
              <div className="tag-list">
                {msg.options.map((opt) => {
                  const isCurrentMessageLastWithOptions =
                    index === lastOptionMessageIndex &&
                    isLastMessageWithOptions;

                  return (
                    <button
                      key={opt.id}
                      className="tag"
                      style={{
                        backgroundColor: "#ff9b3363",
                        cursor: isCurrentMessageLastWithOptions
                          ? "pointer"
                          : "not-allowed",
                        opacity: isCurrentMessageLastWithOptions ? 1 : 0.5,
                      }}
                      disabled={!isCurrentMessageLastWithOptions}
                      onClick={() =>
                        isCurrentMessageLastWithOptions &&
                        handleOptionClick(
                          opt.id,
                          msg.option_type,
                          msg.redirect,
                          msg.redirect_name
                        )
                      }
                    >
                      {opt.value}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {isSending && (
          <div className="message-container">
            <div className="message-wrapper" style={{ alignSelf: "flex-end" }}>
              <div className="user-message" style={{ fontSize: "12px" }}>
                Sending...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />

        {showTextInput && (
          <div className="chat-input">
            <input
              type="text"
              id="userInput"
              placeholder="Ask your query here..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") sendMessage();
              }}
            />
            <button onClick={sendMessage} disabled={!input.trim()}>
              Send
            </button>
          </div>
        )}
      </div>
      {messages.length > 0 && (
        <div className="chat-clear">
          <button onClick={clearChat} className="clear-button">
            Clear Chat
          </button>
        </div>
      )}
    </div>
  );
};
